import { useState } from 'react'
import { useOutletContext, useParams } from 'react-router-dom'
import {
  exportReport,
  useActivityReport,
  useDocumentReport,
  useGroupReport,
  useUserReport,
} from '../api/rooms'
import type { RoomResponse } from '../api/types'
import { errorMessage, formatBytes, formatUtc } from '../lib/format'

type Tab = 'users' | 'documents' | 'groups' | 'activity'

/**
 * The room's activity reports for owners and auditors: by user, by document (the heat
 * map), by group, and the raw log. The window is optional; exports are the same rows
 * as the screen, from the server.
 */
export function Activity() {
  const room = useOutletContext<RoomResponse>()
  const { roomId = '' } = useParams()
  const [tab, setTab] = useState<Tab>('users')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [exportError, setExportError] = useState<string | null>(null)
  const window = {
    from: from ? new Date(from).toISOString() : null,
    to: to ? new Date(to).toISOString() : null,
  }
  const badWindow = window.from !== null && window.to !== null && window.from >= window.to

  const download = (format: 'csv' | 'xlsx', kind?: Tab) => {
    setExportError(null)
    exportReport(roomId, kind ?? tab, format, window).catch((e: unknown) =>
      setExportError(errorMessage(e)),
    )
  }

  return (
    <div className="stack">
      <section className="panel">
        <div className="row-between">
          <h2 className="h2 no-margin">Activity in {room.name}</h2>
          <div className="row-actions">
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => download('csv')}
              disabled={badWindow}
            >
              Export this report (CSV)
            </button>
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => download('xlsx')}
              disabled={badWindow}
            >
              Export this report (Excel)
            </button>
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => {
                setExportError(null)
                exportReport(roomId, 'workbook', 'xlsx', window).catch((e: unknown) =>
                  setExportError(errorMessage(e)),
                )
              }}
              disabled={badWindow}
            >
              Everything (Excel)
            </button>
          </div>
        </div>
        <form className="inline-form" onSubmit={(e) => e.preventDefault()}>
          <label className="small">
            From{' '}
            <input
              type="datetime-local"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              aria-label="From"
            />
          </label>
          <label className="small">
            To{' '}
            <input
              type="datetime-local"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              aria-label="To"
            />
          </label>
          {(from || to) && (
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => {
                setFrom('')
                setTo('')
              }}
            >
              Whole history
            </button>
          )}
          {badWindow && <span className="error-text small">“From” must be before “to”.</span>}
        </form>
        {exportError && <p className="error-text small">{exportError}</p>}
        <nav className="tabs" aria-label="Reports">
          {(['users', 'documents', 'groups', 'activity'] as Tab[]).map((t) => (
            <button
              key={t}
              type="button"
              className={t === tab ? 'tab tab-active' : 'tab'}
              onClick={() => setTab(t)}
            >
              {t === 'users'
                ? 'By user'
                : t === 'documents'
                  ? 'By document'
                  : t === 'groups'
                    ? 'By group'
                    : 'Log'}
            </button>
          ))}
        </nav>
        {badWindow ? null : tab === 'users' ? (
          <UsersTable roomId={roomId} window={window} />
        ) : tab === 'documents' ? (
          <DocumentsTable roomId={roomId} window={window} />
        ) : tab === 'groups' ? (
          <GroupsTable roomId={roomId} window={window} />
        ) : (
          <ActivityLog roomId={roomId} window={window} />
        )}
      </section>
    </div>
  )
}

type Window = { from: string | null; to: string | null }

function UsersTable({ roomId, window }: { roomId: string; window: Window }) {
  const report = useUserReport(roomId, window)
  if (report.isPending) return <p className="muted small">Loading…</p>
  if (report.isError) return <p className="error-text small">{errorMessage(report.error)}</p>
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Person</th>
          <th>Role</th>
          <th>Groups</th>
          <th>Views</th>
          <th>Downloads</th>
          <th>Searches</th>
          <th>Questions</th>
          <th>Refused</th>
          <th>Last active</th>
        </tr>
      </thead>
      <tbody>
        {report.data.rows.map((r) => (
          <tr key={r.userId} className={r.expired ? 'row-expired' : undefined}>
            <td>
              <div>{r.displayName ?? r.email ?? r.userId}</div>
              {r.displayName && r.email && <div className="small muted">{r.email}</div>}
            </td>
            <td>
              <span className="chip">{r.memberRole}</span>
              {r.expired && <span className="chip chip-warn">expired</span>}
            </td>
            <td className="small">{r.groups ?? '—'}</td>
            <td className="mono">{r.views}</td>
            <td className="mono">{r.downloads}</td>
            <td className="mono">{r.searches}</td>
            <td className="mono">{r.questions}</td>
            <td className="mono">{r.denied}</td>
            <td className="small muted">
              {r.lastActivityUtc ? formatUtc(r.lastActivityUtc) : '—'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function DocumentsTable({ roomId, window }: { roomId: string; window: Window }) {
  const report = useDocumentReport(roomId, window)
  if (report.isPending) return <p className="muted small">Loading…</p>
  if (report.isError) return <p className="error-text small">{errorMessage(report.error)}</p>
  const max = Math.max(1, ...report.data.rows.map((r) => r.views + r.downloads))
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Document</th>
          <th>Folder</th>
          <th>Viewers</th>
          <th>Views</th>
          <th>Downloads</th>
          <th>Refused</th>
          <th>Heat</th>
          <th>Last activity</th>
        </tr>
      </thead>
      <tbody>
        {report.data.rows.map((r) => (
          <tr key={r.documentId}>
            <td>
              <div>{r.name}</div>
              <div className="small muted">
                {r.versionNumber ? `v${r.versionNumber} · ` : ''}
                {formatBytes(r.sizeBytes)}
              </div>
            </td>
            <td className="small">
              {r.folderIndex && <span className="mono">{r.folderIndex} </span>}
              {r.folderName ?? 'Room root'}
            </td>
            <td className="mono">{r.viewers}</td>
            <td className="mono">{r.views}</td>
            <td className="mono">{r.downloads}</td>
            <td className="mono">{r.denied}</td>
            <td>
              {/* A <meter>, not a div with a width: the CSP forbids style attributes, and
                  a proportion is what the element is for. */}
              <meter
                className="heat-meter"
                min={0}
                max={max}
                value={r.views + r.downloads}
                aria-label={`${r.views + r.downloads} of ${max}`}
              />
            </td>
            <td className="small muted">
              {r.lastActivityUtc ? formatUtc(r.lastActivityUtc) : '—'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function GroupsTable({ roomId, window }: { roomId: string; window: Window }) {
  const report = useGroupReport(roomId, window)
  if (report.isPending) return <p className="muted small">Loading…</p>
  if (report.isError) return <p className="error-text small">{errorMessage(report.error)}</p>
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Group</th>
          <th>Members</th>
          <th>Active</th>
          <th>Views</th>
          <th>Downloads</th>
          <th>Searches</th>
          <th>Questions</th>
          <th>Last activity</th>
        </tr>
      </thead>
      <tbody>
        {report.data.rows.map((r) => (
          <tr key={r.groupId ?? 'none'}>
            <td>{r.groupName}</td>
            <td className="mono">{r.members}</td>
            <td className="mono">{r.activeMembers}</td>
            <td className="mono">{r.views}</td>
            <td className="mono">{r.downloads}</td>
            <td className="mono">{r.searches}</td>
            <td className="mono">{r.questions}</td>
            <td className="small muted">
              {r.lastActivityUtc ? formatUtc(r.lastActivityUtc) : '—'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function ActivityLog({ roomId, window }: { roomId: string; window: Window }) {
  const [page, setPage] = useState(0)
  const [action, setAction] = useState('')
  const take = 100
  const report = useActivityReport(roomId, {
    ...window,
    action: action || null,
    skip: page * take,
    take,
  })
  if (report.isPending) return <p className="muted small">Loading…</p>
  if (report.isError) return <p className="error-text small">{errorMessage(report.error)}</p>
  const pages = Math.max(1, Math.ceil(report.data.total / take))
  return (
    <div className="stack-sm">
      <div className="inline-form">
        <label className="small">
          Actions{' '}
          <select
            value={action}
            onChange={(e) => {
              setAction(e.target.value)
              setPage(0)
            }}
          >
            <option value="">all</option>
            <option value="document.">documents</option>
            <option value="qa.">Q&amp;A</option>
            <option value="permission.">permissions and refusals</option>
            <option value="room.">room</option>
            <option value="nda.">NDA</option>
            <option value="invitation.">invitations</option>
            <option value="report.">report reads</option>
          </select>
        </label>
        <span className="small muted">
          {report.data.total} event{report.data.total === 1 ? '' : 's'} · page {page + 1} of {pages}
        </span>
        <button
          type="button"
          className="btn btn-sm"
          disabled={page === 0}
          onClick={() => setPage(page - 1)}
        >
          Newer
        </button>
        <button
          type="button"
          className="btn btn-sm"
          disabled={page + 1 >= pages}
          onClick={() => setPage(page + 1)}
        >
          Older
        </button>
      </div>
      <table className="table">
        <thead>
          <tr>
            <th>When (UTC)</th>
            <th>Who</th>
            <th>What</th>
            <th>On</th>
            <th>Outcome</th>
            <th>From</th>
          </tr>
        </thead>
        <tbody>
          {report.data.events.map((e) => (
            <tr key={e.eventId}>
              <td className="small mono">{formatUtc(e.eventUtc)}</td>
              <td className="small">{e.actorName ?? e.actorEmail ?? '—'}</td>
              <td className="small mono">{e.action}</td>
              <td className="small">
                {e.targetName ?? e.targetType ?? '—'}
                {e.targetName && e.targetType && <span className="muted"> ({e.targetType})</span>}
              </td>
              <td>
                <span className={e.outcome === 'Success' ? 'chip' : 'chip chip-bad'}>
                  {e.outcome}
                </span>
              </td>
              <td className="small muted mono">{e.ipAddress ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
